/**
 * One Waterlily field on the mapping screen: what it is, the CRM field chosen for it, and where it stands.
 *
 * The line under the name says the one thing worth knowing about the row as it is now: why a match
 * was proposed, that the mapped field has gone from the CRM, that nothing in the CRM looks like it and
 * what that costs, or that it fills itself from related records.
 *
 * A candidate the matcher was not sure enough of to pre-select is offered as a sentence with a
 * button, so taking it is one click and ignoring it is none.
 */
import { AlertTriangle, Check, CircleDashed } from 'lucide-react';
import { Badge, Button } from '@design-system';
import type { Prospecting } from '@client/api/types';
import type { FieldMapRow as Row } from '@client/hooks/useCrmFieldMap';
import { fieldMapCopy } from '../lib/field-map-copy';
import { UNMAPPED } from '../lib/field-map-groups';
import { FieldPicker } from './field-picker';
import { ValueMeaning } from './value-meaning';

interface FieldMapRowProps {
  row: Row;
  fields: Prospecting.DiscoveredField[];
  crmName: string;
  disabled: boolean;
  onSelect: (canonicalField: string, sourceField: string) => void;
  onMeaning: (canonicalField: string, semantics: Prospecting.FieldSemantics) => void;
}

interface RowStatusProps {
  row: Row;
}

function RowStatus({ row }: RowStatusProps) {
  const { status } = fieldMapCopy;
  const untouched = row.selected === row.saved;

  if (row.sourceMissing && untouched) {
    return (
      <Badge variant="destructive">
        <AlertTriangle />
        {status.sourceMissing}
      </Badge>
    );
  }
  if (row.selected === UNMAPPED && row.isPending) return <Badge variant="secondary">{status.changed}</Badge>;
  if (row.selected === UNMAPPED) {
    return row.isDerived ? (
      <span className="inline-flex items-center gap-1 text-sm text-gray-600">
        <Check className="h-3.5 w-3.5" />
        {status.derived}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 text-sm text-gray-500">
        <CircleDashed className="h-3.5 w-3.5" />
        {status.notMapped}
      </span>
    );
  }
  if (row.needsScale) return <Badge variant="warning">{status.needsUnit}</Badge>;
  if (row.missingYesRule) return <Badge variant="warning">{status.needsRule}</Badge>;
  if (row.isPending) {
    return <Badge variant="secondary">{row.status === 'suggested' && untouched ? status.check : status.changed}</Badge>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-sm text-green-700">
      <Check className="h-3.5 w-3.5" />
      {status.confirmed}
    </span>
  );
}

function captionFor(row: Row, crmName: string): string | null {
  const untouched = row.selected === row.saved;

  if (row.sourceMissing && untouched) return fieldMapCopy.sourceMissing(row.saved, crmName);
  if (row.selected !== UNMAPPED) {
    return row.status === 'suggested' && untouched ? fieldMapCopy.matchReason(row.matchedOn) : null;
  }
  if (row.isDerived) return fieldMapCopy.derived(crmName);

  const why =
    row.status === 'rejected'
      ? fieldMapCopy.leftUnmapped
      : row.candidates.length === 0
        ? fieldMapCopy.noMatch(crmName)
        : null;
  return [why, fieldMapCopy.cost(row.canonicalField, row.role)].filter(Boolean).join(' ') || null;
}

export function FieldMapRow({ row, fields, crmName, disabled, onSelect, onMeaning }: FieldMapRowProps) {
  const caption = captionFor(row, crmName);
  const [offer] = row.selected === UNMAPPED && row.status !== 'rejected' ? row.candidates : [];

  return (
    <li className="grid gap-3 px-4 py-4 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)_9.5rem] md:gap-6">
      <div>
        <p className="font-medium text-gray-900">{row.label}</p>
        {caption && <p className="mt-0.5 text-sm text-gray-600">{caption}</p>}
      </div>

      <div className="min-w-0">
        <FieldPicker
          row={row}
          fields={fields}
          crmName={crmName}
          disabled={disabled}
          onSelect={sourceField => onSelect(row.canonicalField, sourceField)}
        />
        {offer && (
          <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-700">
            <span>
              {fieldMapCopy.suggestionLead}{' '}
              <span className="font-medium text-gray-900">{offer.sourceLabel ?? offer.sourceField}</span>
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() => onSelect(row.canonicalField, offer.sourceField)}
            >
              {fieldMapCopy.useSuggestion}
            </Button>
          </p>
        )}
        <ValueMeaning
          row={row}
          crmName={crmName}
          disabled={disabled}
          onChange={semantics => onMeaning(row.canonicalField, semantics)}
        />
      </div>

      <div className="md:pt-1.5 md:text-right">
        <RowStatus row={row} />
      </div>
    </li>
  );
}
